import { useCallback, useEffect, useRef, useState } from 'react'
import { useActiveAnalysis } from '../contexts/ActiveAnalysisContext'

export type UpdateState = 'hidden' | 'default' | 'deferred' | 'reloading' | 'success'

export interface UseUpdateAvailableResult {
  state: UpdateState
  newVersion: string | null
  confirmReload: () => void
}

const POLL_INTERVAL_MS = 60_000
const RELOAD_DELAY_MS = 1500
const SUCCESS_HOLD_MS = 3500
const SESSION_KEY = 'bc-update-success'
const VERSION_URL = '/version.json'

/**
 * useUpdateAvailable — production bundle freshness watchdog.
 *
 * On mount:
 *   1. If sessionStorage has `bc-update-success` we just came back from a
 *      reload — start in 'success' for SUCCESS_HOLD_MS then fade to 'hidden'.
 *      Clear the flag immediately so a second tab never falsely shows success.
 *   2. Otherwise start in 'hidden' and begin the poll loop against
 *      /version.json?t=<now>. First check runs immediately. Subsequent
 *      checks every 60 s. Re-check on visibilitychange -> visible and
 *      pageshow with persisted=true (BFCache restore).
 *
 * Version comparison: strict equality against import.meta.env.VITE_APP_VERSION.
 * If the fetched version differs, transition to 'deferred' when the user has
 * any analysis in progress, otherwise to 'default'. A listener on the
 * ActiveAnalysisContext handles the auto-switch when processing count changes.
 *
 * confirmReload(): caller-triggered transition. Moves to 'reloading', waits
 * RELOAD_DELAY_MS (1500 ms) for the slash-spin animation to register, writes
 * the sessionStorage flag, then window.location.reload(). The fresh page
 * then picks up the flag and enters 'success'.
 *
 * Dev mode: short-circuits in import.meta.env.DEV because the dev-server
 * VITE_APP_VERSION is the pre-build one and the on-disk public/version.json
 * is the last build's value — comparing them causes reload loops.
 */
export function useUpdateAvailable(): UseUpdateAvailableResult {
  const { analyses } = useActiveAnalysis()
  const hasProcessing = analyses.some((a) => a.status === 'processing')
  const hasProcessingRef = useRef(hasProcessing)
  // Sync the ref in an effect so we don't mutate during render.
  useEffect(() => {
    hasProcessingRef.current = hasProcessing
  }, [hasProcessing])

  const [state, setState] = useState<UpdateState>(() => {
    if (typeof window === 'undefined') return 'hidden'
    try {
      if (sessionStorage.getItem(SESSION_KEY) === '1') {
        sessionStorage.removeItem(SESSION_KEY)
        return 'success'
      }
    } catch {
      /* sessionStorage unavailable — fall through */
    }
    return 'hidden'
  })
  const [newVersion, setNewVersion] = useState<string | null>(null)

  // Flag set by checkVersion the first time it detects a mismatch. Once set,
  // changes in the processing state flip the modal between default and
  // deferred automatically.
  const updateDetectedRef = useRef(false)

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Auto-transition default <-> deferred when the processing set changes.
  // Never touch reloading/success/hidden — those are owned by other paths.
  // The setState call here is intentional: when the user's analysis
  // completes mid-modal, the modal must flip from deferred to default so
  // the user can click "Recharger maintenant". Guarded by updateDetectedRef
  // so it's a no-op when no update has been detected yet.
  useEffect(() => {
    if (!updateDetectedRef.current) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState((prev) => {
      if (prev === 'reloading' || prev === 'success' || prev === 'hidden') {
        return prev
      }
      return hasProcessing ? 'deferred' : 'default'
    })
  }, [hasProcessing])

  const checkVersion = useCallback(async () => {
    if (import.meta.env.DEV) return
    try {
      const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' })
      if (!res.ok) return
      const data: unknown = await res.json()
      if (
        typeof data !== 'object' ||
        data === null ||
        typeof (data as { version?: unknown }).version !== 'string'
      ) {
        return
      }
      const fetched = (data as { version: string }).version
      const current = import.meta.env.VITE_APP_VERSION ?? ''
      if (!current || fetched === current) return

      updateDetectedRef.current = true
      setNewVersion(fetched)
      setState((prev) => {
        if (prev === 'reloading' || prev === 'success') return prev
        return hasProcessingRef.current ? 'deferred' : 'default'
      })
    } catch {
      /* silent — retry next tick */
    }
  }, [])

  // Success auto-fade: schedule the hidden transition.
  useEffect(() => {
    if (state !== 'success') return
    successTimerRef.current = setTimeout(() => {
      setState('hidden')
    }, SUCCESS_HOLD_MS)
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current)
      successTimerRef.current = null
    }
  }, [state])

  // Polling + lifecycle listeners — mounted once per hook instance.
  // The setState calls inside checkVersion happen asynchronously from a
  // fetch callback, not synchronously in the effect body — safe but the
  // static lint cannot distinguish the two cases (see same pattern in
  // src/components/crm/KanbanBoard.tsx:88 and 4 other CRM files).
  useEffect(() => {
    if (import.meta.env.DEV) return

    // Mount-time immediate check so a stale tab detects an update
    // as soon as the user loads a new route.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void checkVersion()

    intervalRef.current = setInterval(() => {
      void checkVersion()
    }, POLL_INTERVAL_MS)

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void checkVersion()
      }
    }
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        void checkVersion()
      }
    }

    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pageshow', onPageShow)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [checkVersion])

  // Clean up any pending reload timer on unmount so tests never trigger a
  // ghost reload.
  useEffect(() => {
    return () => {
      if (reloadTimerRef.current) {
        clearTimeout(reloadTimerRef.current)
        reloadTimerRef.current = null
      }
    }
  }, [])

  const confirmReload = useCallback(() => {
    setState((prev) => {
      if (prev !== 'default') return prev
      return 'reloading'
    })
    reloadTimerRef.current = setTimeout(() => {
      try {
        sessionStorage.setItem(SESSION_KEY, '1')
      } catch {
        /* best effort */
      }
      window.location.reload()
    }, RELOAD_DELAY_MS)
  }, [])

  return { state, newVersion, confirmReload }
}
